import { useEffect, useRef, useState } from 'react';
import { cloneDeep, isEqual, omit } from 'lodash';

import FilterRegistry from '../../page-framework/filter/FilterRegistry';
import { timeRangeFilterId } from '../../filters';
import { CUSTOM_ID } from '../../filters/time-range-filter/TimeRangeFilterConstants';
import { useFilterState } from '../../../stores/FilterStateStore';

function filterStateChanged(filterState) {
    // We're replacing the time range, so exclude the time range filter value from the
    // state we check for changes, since we only care if the other filters changed.
    const previousState = omit(filterState.previous, [timeRangeFilterId]);
    const currentState = omit(filterState.current, [timeRangeFilterId]);

    return !isEqual(previousState, currentState);
}

function convertTimeRangeSelection(timeRangeSelection) {
    // The time range filter state uses Dates, so convert the timestamps to Dates.
    return {
        start: new Date(timeRangeSelection.start),
        end: new Date(timeRangeSelection.end),
    };
}

/**
 * A hook to build new filter state that uses the `StatusBreakout` time range selection in place of
 * any existing time range filter state.
 *
 * @param timeRangeSelection The time range selected in the `StatusBreakout` chart.
 * @param useSelection A boolean indicating whether to use the selected time range.  If false, the time
 * range in filter state will be used instead.  This flag is used when the `StatusBreakout` chart is
 * not shown, and so there is no time range selection available.
 */
export function useTimeRangeSelection(timeRangeSelection, useSelection = true) {
    const [filterState] = useFilterState();
    const [newFilterState, setNewFilterState] = useState(undefined);
    const previousTimeRangeSelection = useRef();
    const applyTimeRangeSelection = useRef();

    applyTimeRangeSelection.current = () => {
        if (!filterState || !timeRangeSelection) return;

        // Clone the source filter state so it isn't accidentally mutated by changes to the new filter state.
        const sourceFilterState = cloneDeep(filterState);
        // Convert the StatusBreakout time range selection into a custom time range for the
        // time range filter.
        const timeRangeFilterStateService = FilterRegistry.getFilterStateService(timeRangeFilterId);
        const timeRange = convertTimeRangeSelection(timeRangeSelection);
        const timeRangeFilterState = {
            id: CUSTOM_ID,
            ...timeRange,
        };

        // Replace the original time range filter value with the time range selection value.
        setNewFilterState({
            ...sourceFilterState,
            previous: previousTimeRangeSelection.current
                ? {
                      ...sourceFilterState.previous,
                      [timeRangeFilterId]: {
                          id: CUSTOM_ID,
                          ...convertTimeRangeSelection(previousTimeRangeSelection.current),
                      },
                  }
                : omit(sourceFilterState.previous, timeRangeFilterId),
            current: {
                ...sourceFilterState.current,
                [timeRangeFilterId]: timeRangeFilterState,
            },
            encoded: {
                ...sourceFilterState.encoded,
                [timeRangeFilterId]: timeRangeFilterStateService.encode(timeRangeFilterState),
            },
            summarized: {
                ...sourceFilterState.summarized,
                [timeRangeFilterId]: timeRangeFilterStateService.summarize(timeRangeFilterState),
            },
            parameters: {
                ...sourceFilterState.parameters,
                [timeRangeFilterId]: timeRangeFilterStateService.parameterize(timeRangeFilterState),
            },
        });
    };

    useEffect(() => {
        if (useSelection) {
            applyTimeRangeSelection.current();
            previousTimeRangeSelection.current = timeRangeSelection;
        }
    }, [timeRangeSelection, useSelection]);

    useEffect(() => {
        if (!useSelection) {
            // Just apply the filter state unchanged, since we're not using the selection.
            setNewFilterState(filterState);
        } else if (filterState && filterStateChanged(filterState)) {
            // We're using the selection, and the filter state changed, so apply the selection to the
            // changed filter state.
            applyTimeRangeSelection.current();
        }
    }, [filterState, useSelection]);

    return newFilterState;
}
